import { addDays } from 'date-fns';
import { isEqual, compact } from 'lodash-es';
import { useFormContext } from 'react-hook-form';
import { getServiceChangeBullets } from '~/business-logic/amendmentsLogic';
import { approvalMethodNoticePeriodModel, paymentMethodModel, relationshipModel, serviceBillingModel, serviceCostModel, serviceModel, timeModel } from '~/models';
import { makeServiceQboClassChange } from '~/models/agreement-change';
export const useEditAgreementForm = ()=>useFormContext();
export const editAgreementTextsPath = 'components.editAgreementModal';
export const getApprovalCalloutText = (amendmentsApprovalMethod, editTexts)=>{
    return approvalMethodNoticePeriodModel.isAutoApproval(amendmentsApprovalMethod) ? {
        type: 'auto',
        text: editTexts.approvalCallout.autoApprovalCalloutAfterNoticeDays({
            count: amendmentsApprovalMethod.auto.noticePeriodDays
        })
    } : {
        type: 'manual',
        text: editTexts.approvalCallout.manualApprovalCallout()
    };
};
export const getServiceAutoApprovalDate = (service, relationship, now)=>{
    if (serviceModel.hasApprovalMethod(service)) {
        const autoApprovalDate = serviceModel.autoApprovalDate(service);
        return autoApprovalDate ? new Date(autoApprovalDate) : null;
    }
    return getAutoApprovalDateFromRelationship(relationship, now);
};
export function willEmailBeSent(options) {
    const { services, originalServicesMap, relationship, updatedNetTerms, isSelectedLegalTermsDirty, amendmentsApprovalMethod, invoicesApprovalMethod, allowManualPayments, coverCreditCardFees, t } = options;
    const isCreditCardSelected = !!relationship.paymentMethod && paymentMethodModel.isCreditCard(relationship.paymentMethod);
    const coverCCFeesWillSendEmail = coverCreditCardFees !== relationshipModel.isVendorCoveringCCFee(relationship) && coverCreditCardFees !== relationshipModel.updatedIsVendorCoveringCCFeeValue(relationship) && isCreditCardSelected;
    const allowManualPaymentsWillSendEmail = allowManualPayments !== relationship.allowManualPayments && allowManualPayments !== relationshipModel.updatedAllowManualPaymentsValue(relationship);
    return isSelectedLegalTermsDirty || relationshipModel.actualNetTerms(relationship) !== updatedNetTerms || !approvalMethodNoticePeriodModel.isEqual(relationshipModel.actualAmendmentsApprovalMethod(relationship), amendmentsApprovalMethod) || !approvalMethodNoticePeriodModel.isEqual(relationshipModel.actualInvoicesApprovalMethod(relationship), invoicesApprovalMethod) || allowManualPaymentsWillSendEmail || coverCCFeesWillSendEmail || services.some((s)=>{
        const originalService = originalServicesMap.get(s.id);
        const isNewlyTerminated = serviceModel.isTerminated(s.status) && !!originalService && !serviceModel.isTerminated(originalService.status);
        const isExistingNewService = serviceModel.isAwaitingApproval(s.status) && !!originalService && serviceModel.isAwaitingApproval(originalService.status);
        return !isExistingNewService && (isNewlyTerminated || !isEqual(originalService, s) && getServiceChangeBullets({
            service: s,
            t
        }).some((b)=>!b.emailWontBeSent));
    });
}
function getAutoApprovalDateFromRelationship(relationship, now) {
    return approvalMethodNoticePeriodModel.isAutoApproval(relationship.amendmentsApprovalMethod) ? addDays(now, relationship.amendmentsApprovalMethod.auto.noticePeriodDays) : null;
}
export function getAmendedNextBillingDateText(service, amendedNextBillingDate, texts) {
    const date = timeModel.formatDate(amendedNextBillingDate);
    const isYearly = serviceBillingModel.isYearly(service.billing);
    const isPaused = serviceModel.isPaused(service);
    if (isYearly) return isPaused ? texts.yearlyPaused({
        date
    }) : texts.yearly({
        date
    });
    return isPaused ? texts.defaultPaused({
        date
    }) : texts.default({
        date
    });
}
export function changeServicePriceAndDiscount(service, cost, originalService) {
    const { isAwaitingApproval, setPrice, setPriceChanges, setDiscount, setDiscountChanges } = serviceModel;
    const updatedService = isAwaitingApproval(service.status) ? setPrice(setDiscount(service, cost.discount), cost) : setPriceChanges(setDiscountChanges(service, cost.discount), cost);
    if (originalService && !isAwaitingApproval(updatedService.status)) {
        return handleDiscountExpiration(updatedService, originalService);
    }
    return updatedService;
}
export function handleDiscountExpiration(editedService, originalService) {
    var _originalService_cost_discount;
    if (!serviceModel.isAmended(editedService.status)) {
        return editedService;
    }
    const shouldShowWarning = shouldShowDiscountExpirationWarning(editedService, originalService);
    const originalExpiration = originalService === null || originalService === void 0 ? void 0 : (_originalService_cost_discount = originalService.cost.discount) === null || _originalService_cost_discount === void 0 ? void 0 : _originalService_cost_discount.expiration();
    if (shouldShowWarning) {
        var _ref;
        var _editedService_status_amended_changes_cost_discount, _editedService_status_amended_changes_cost;
        return serviceModel.setDiscountChanges(editedService, (_ref = (_editedService_status_amended_changes_cost = editedService.status.amended.changes.cost) === null || _editedService_status_amended_changes_cost === void 0 ? void 0 : (_editedService_status_amended_changes_cost_discount = _editedService_status_amended_changes_cost.discount) === null || _editedService_status_amended_changes_cost_discount === void 0 ? void 0 : _editedService_status_amended_changes_cost_discount.setExpiration(undefined)) !== null && _ref !== void 0 ? _ref : null);
    } else if (originalExpiration) {
        var _ref1;
        var _editedService_status_amended_changes_cost_discount1, _editedService_status_amended_changes_cost1;
        return serviceModel.setDiscountChanges(editedService, (_ref1 = (_editedService_status_amended_changes_cost1 = editedService.status.amended.changes.cost) === null || _editedService_status_amended_changes_cost1 === void 0 ? void 0 : (_editedService_status_amended_changes_cost_discount1 = _editedService_status_amended_changes_cost1.discount) === null || _editedService_status_amended_changes_cost_discount1 === void 0 ? void 0 : _editedService_status_amended_changes_cost_discount1.setExpiration(originalExpiration)) !== null && _ref1 !== void 0 ? _ref1 : null);
    }
    return editedService;
}
export function makeServiceQboClassChanges(services, originalServices, qboClasses) {
    const qboClassesMap = new Map(qboClasses === null || qboClasses === void 0 ? void 0 : qboClasses.map((c)=>[
            c.id,
            c
        ]));
    const originalServicesMap = new Map(originalServices.map((s)=>[
            s.id,
            s
        ]));
    const getQboClassEssentialData = (id)=>{
        const qboClass = id ? qboClassesMap.get(id) : null;
        return qboClass ? {
            id: qboClass.id,
            name: qboClass.name
        } : null;
    };
    return compact(services.map((s)=>{
        const originalService = originalServicesMap.get(s.id);
        const newId = serviceModel.integratedClassId(s);
        const oldId = originalService ? serviceModel.integratedClassId(originalService) : null;
        if (newId === oldId) {
            return null;
        }
        return makeServiceQboClassChange(s.id, getQboClassEssentialData(oldId), getQboClassEssentialData(newId));
    }));
}
export function showOnApprovalInfo(service, originalService) {
    const isUnsaved = !isEqual(originalService, service);
    const isAutoFirstOneTimeInStages = serviceBillingModel.isOneTimeInStages(service.billing) && service.billing.oneTimeInStages.isAutoFirst();
    const isOnApproval = serviceBillingModel.isOnApproval(service.billing);
    const isAwaitingApproval = serviceModel.isAwaitingApproval(service.status);
    return isAwaitingApproval && isUnsaved && (isAutoFirstOneTimeInStages || isOnApproval);
}
export function shouldShowDiscountExpirationWarning(service, originalService) {
    var _ref;
    var _originalCost_discount_expiration, _originalCost_discount;
    if (!originalService || !serviceModel.isAmended(service.status)) return false;
    const amendedCost = service.status.amended.changes.cost;
    const originalCost = originalService.cost;
    const hadExpiration = (_ref = (_originalCost_discount = originalCost.discount) === null || _originalCost_discount === void 0 ? void 0 : (_originalCost_discount_expiration = _originalCost_discount.expiration()) === null || _originalCost_discount_expiration === void 0 ? void 0 : _originalCost_discount_expiration.hasExpiration()) !== null && _ref !== void 0 ? _ref : false;
    return !!(amendedCost && !serviceCostModel.isDiscountAndRateEqual(originalCost, amendedCost) && hadExpiration);
}
export function disableEditBillingInitiation(param) {
    let { originalService } = param;
    const isFreshlyAdded = !originalService;
    if (!isFreshlyAdded) {
        return {
            reason: 'existingServiceInAgreement'
        };
    }
    return false;
}
